import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';

import {
  Home, Users, FileText, Map,
  BarChart3, LogOut, Settings,
  CreditCard, Menu, X, FolderOpen,
  ChevronLeft, ChevronRight, MessageSquare,
  Plus, List, ClipboardList, PieChart, ChevronDown, Shield,
  MapPin, Building2, UserPlus, FileSignature, Plane, StickyNote, DollarSign, Receipt, Wallet, Zap,
  GraduationCap, CheckSquare, Calendar, Inbox, Activity, Crosshair
} from 'lucide-react';
import toast from 'react-hot-toast';
import { logActivity } from '../../services/activityLog.service';

interface NavigationProps {
  children?: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: any;
  roles: string[];
  children?: NavItem[];
}

const Navigation: React.FC<NavigationProps> = ({ children }) => {
  const { logout, userProfile, currentUser } = useAuth();
  const { projects, selectedProjectId, selectedProject, setSelectedProjectId } = useProject();
  const { hasUnread: hasChatUnread } = useChatNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set([]));
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  const allRoles = ['field', 'client_user', 'client_admin', 'provider_user', 'provider_admin'];
  // Provider-only roles (no client roles)
  const providerRoles = ['field', 'provider_user', 'provider_admin'];

  const navItems: NavItem[] = [
    // Top-level items (no dropdown)
    { path: '/', label: 'Dashboard', icon: Home, roles: allRoles },
    { path: '/chat', label: 'Chat', icon: MessageSquare, roles: allRoles },
    { path: '/tasks', label: 'Tasks', icon: CheckSquare, roles: allRoles },
    { path: '/accountability', label: 'Live Map', icon: MapPin, roles: ['provider_user', 'provider_admin'] },

    // Field Work dropdown — field, provider_user, provider_admin
    {
      path: '/field-work',
      label: 'Field Work',
      icon: MapPin,
      roles: providerRoles,
      children: [
        { path: '/leads', label: 'Leads', icon: FileText, roles: ['field'] },
        { path: '/leads/new', label: 'Add Lead', icon: Plus, roles: providerRoles },
        { path: '/canvass', label: 'Canvass', icon: MapPin, roles: providerRoles },
        { path: '/seek-find', label: 'Seek & Find', icon: Crosshair, roles: providerRoles },
        { path: '/events', label: 'Events', icon: Calendar, roles: providerRoles },
        { path: '/training', label: 'Training', icon: GraduationCap, roles: providerRoles },
        { path: '/logistics', label: 'Logistics', icon: Plane, roles: providerRoles },
        { path: '/expenses', label: 'Reimbursements', icon: Receipt, roles: ['field'] },
        { path: '/payment-history', label: 'My Pay', icon: Wallet, roles: ['field'] },
      ]
    },

    // Client Tools dropdown — client_user, client_admin, provider_user, provider_admin
    {
      path: '/client-tools',
      label: 'Client Tools',
      icon: FolderOpen,
      roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'],
      children: [
        { path: '/leads', label: 'Leads', icon: FileText, roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'] },
        { path: '/schedule', label: 'Schedule', icon: Calendar, roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'] },
        { path: '/appointments', label: 'Appointments', icon: Calendar, roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'] },
        { path: '/maps', label: 'Maps', icon: Map, roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'] },
        { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['client_user', 'client_admin', 'provider_user', 'provider_admin'] },
      ]
    },

    // Management dropdown — client_admin, provider_user, provider_admin
    {
      path: '/management',
      label: 'Management',
      icon: Shield,
      roles: ['client_admin', 'provider_user', 'provider_admin'],
      children: [
        { path: '/users', label: 'Users', icon: Users, roles: ['client_admin', 'provider_user', 'provider_admin'] },
        { path: '/workloads/manage', label: 'Workload Management', icon: Map, roles: ['client_admin', 'provider_user', 'provider_admin'] },
        { path: '/seek-find/data', label: 'Seek & Find Data', icon: Crosshair, roles: ['client_admin', 'provider_user', 'provider_admin'] },
        { path: '/surveys', label: 'Surveys', icon: FileText, roles: ['provider_user', 'provider_admin'] },
        { path: '/project-notes', label: 'Project Notes', icon: StickyNote, roles: ['client_admin', 'provider_user', 'provider_admin'] },
        { path: '/activity-log', label: 'Activity Log', icon: Activity, roles: ['client_admin', 'provider_user', 'provider_admin'] },
      ]
    },

    // Admin dropdown — provider_user, provider_admin
    {
      path: '/admin',
      label: 'Admin',
      icon: Settings,
      roles: ['provider_user', 'provider_admin'],
      children: [
        { path: '/organizations', label: 'Organizations', icon: Building2, roles: ['provider_admin'] },
        { path: '/projects', label: 'Projects', icon: FolderOpen, roles: ['provider_user', 'provider_admin'] },
        { path: '/contracts', label: 'Contracts', icon: FileSignature, roles: ['provider_user', 'provider_admin'] },
        { path: '/fiscal/ledger', label: 'Fiscal', icon: DollarSign, roles: ['provider_admin'] },
        { path: '/billing', label: 'Billing', icon: CreditCard, roles: ['provider_admin'] },
        { path: '/invoices', label: 'Invoices', icon: Receipt, roles: ['provider_admin'] },
        { path: '/expenses', label: 'Expenses', icon: Wallet, roles: ['provider_admin', 'provider_user'] },
        { path: '/staff-payments', label: 'Staff Payments', icon: Users, roles: ['provider_admin', 'provider_user'] },
        { path: '/payment-history', label: 'Payment History', icon: FileText, roles: ['provider_admin'] },
        { path: '/financials', label: 'Financials', icon: BarChart3, roles: ['provider_admin'] },
        { path: '/consult-requests', label: 'Consult Requests', icon: Inbox, roles: ['provider_admin'] },
        { path: '/automations', label: 'Automations', icon: Zap, roles: ['provider_admin'] },
      ]
    },

    // Settings (bottom)
    { path: '/settings', label: 'Settings', icon: Settings, roles: allRoles },
  ];

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      if (userProfile) {
        logActivity({ userId: userProfile.uid, userName: userProfile.name || userProfile.email, userRole: userProfile.role, action: 'logout', entityType: 'session' });
      }
      await logout();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  const userRole = userProfile?.role || 'field';

  // Build visible items based on role
  const visibleItems = React.useMemo(() => {
    return navItems.filter(item => item.roles.includes(userRole));
  }, [navItems, userRole]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Desktop Navigation */}
      <div className={`hidden md:fixed md:inset-y-0 md:flex md:flex-col transition-all duration-300 ease-in-out z-40 ${isCollapsed ? 'md:w-16' : 'md:w-64'}`}>
        <div className="flex min-h-0 flex-1 flex-col bg-gradient-to-b from-slate-600 via-slate-700 to-slate-800 shadow-xl">
          <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
            <div className={`flex flex-shrink-0 items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-4 mb-4`}>
              {!isCollapsed && (
                <h1 className="text-2xl font-bold text-white">
                  Canvassed
                </h1>
              )}
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className={`text-slate-300 hover:text-white transition-colors p-1 rounded hover:bg-slate-600 ${isCollapsed ? 'mx-auto' : ''}`}
                title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
              >
                {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
            </div>

            {/* Global Project Selector */}
            {!isCollapsed && projects.length > 0 && (
              <div className="px-4 mb-4">
                <label className="block text-xs font-medium text-slate-400 mb-1 px-1">Active Project</label>
                <div className="relative">
                  <button
                    onClick={() => setShowProjectSelector(!showProjectSelector)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-500/30 border border-slate-500 rounded-lg text-sm text-white hover:bg-slate-500/50 transition-colors"
                  >
                    <span className="truncate">{selectedProject?.name || 'Select Project'}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-300 transition-transform ${showProjectSelector ? 'rotate-180' : ''}`} />
                  </button>
                  {showProjectSelector && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          onClick={() => {
                            setSelectedProjectId(project.id);
                            setShowProjectSelector(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-600 transition-colors ${
                            selectedProjectId === project.id ? 'bg-slate-600 text-white font-medium' : 'text-slate-300'
                          }`}
                        >
                          {project.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <nav className={`flex-1 space-y-1 ${isCollapsed ? 'px-2' : 'px-4'}`}>
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.path);
                const visibleChildren = hasChildren
                  ? item.children!.filter((child) => child.roles.includes(userRole))
                  : [];

                if (hasChildren && visibleChildren.length > 0) {
                  return (
                    <div key={item.path}>
                      <button
                        onClick={() => toggleExpanded(item.path)}
                        className={`
                          w-full group flex items-center text-sm font-medium rounded-lg transition-all duration-200
                          ${isCollapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2.5'}
                          ${active
                            ? 'bg-white/20 text-white'
                            : 'text-slate-300 hover:bg-white/10 hover:text-white'
                          }
                        `}
                        title={isCollapsed ? item.label : ''}
                      >
                        <div className="relative">
                          <Icon className={`flex-shrink-0 ${isCollapsed ? 'h-5 w-5' : 'mr-3 h-5 w-5'}`} />
                        </div>
                        <span className={`flex-1 text-left transition-all duration-300 ${isCollapsed ? 'sr-only' : 'block'}`}>
                          {item.label}
                        </span>
                        {!isCollapsed && (
                          <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        )}
                      </button>
                      {isExpanded && !isCollapsed && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-500 pl-2">
                          {visibleChildren.map((child) => {
                            const ChildIcon = child.icon;
                            const childActive = isActive(child.path);
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`
                                  group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                                  ${childActive
                                    ? 'bg-white/20 text-white'
                                    : 'text-slate-400 hover:bg-white/10 hover:text-white'
                                  }
                                `}
                              >
                                <ChildIcon className="mr-3 h-4 w-4 flex-shrink-0" />
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      group flex items-center text-sm font-medium rounded-lg transition-all duration-200
                      ${isCollapsed ? 'px-2 py-2 justify-center' : 'px-3 py-2.5'}
                      ${active
                        ? 'bg-white/20 text-white'
                        : 'text-slate-300 hover:bg-white/10 hover:text-white'
                      }
                    `}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="relative">
                      <Icon className={`flex-shrink-0 ${isCollapsed ? 'h-5 w-5' : 'mr-3 h-5 w-5'}`} />
                      {item.path === '/chat' && hasChatUnread && isCollapsed && (
                        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-red-500 rounded-full border border-slate-700" />
                      )}
                    </div>
                    <span className={`transition-all duration-300 ${isCollapsed ? 'sr-only' : 'block'}`}>
                      {item.label}
                    </span>
                    {item.path === '/chat' && hasChatUnread && !isCollapsed && (
                      <span className="ml-auto h-2 w-2 bg-red-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex flex-shrink-0 border-t border-slate-600 p-4">
            <button
              onClick={handleLogout}
              className={`group flex items-center text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white rounded-lg transition-all duration-200
                ${isCollapsed ? 'justify-center w-full px-2 py-2' : 'w-full px-2 py-2'}
              `}
              title={isCollapsed ? 'Logout' : ''}
            >
              <LogOut className={`flex-shrink-0 ${isCollapsed ? 'h-5 w-5' : 'mr-3 h-5 w-5'}`} />
              <span className={`transition-all duration-300 ${isCollapsed ? 'sr-only' : 'block'}`}>
                Logout
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-slate-700 to-slate-800 md:hidden border-b border-slate-600">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-white">Canvassed</h1>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-slate-300 hover:text-white"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-gray-600 bg-opacity-75 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Mobile menu panel */}
      <div className={`
        fixed top-0 left-0 bottom-0 z-40 w-64 bg-gradient-to-b from-slate-600 via-slate-700 to-slate-800 shadow-xl transform transition-transform duration-300 ease-in-out md:hidden
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between p-4 border-b border-slate-600">
            <h1 className="text-xl font-bold text-white">Canvassed</h1>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-slate-300 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Mobile Project Selector */}
          {projects.length > 0 && (
            <div className="px-4 py-3 border-b border-slate-600">
              <label className="block text-xs font-medium text-slate-400 mb-1">Active Project</label>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-500/30 border border-slate-500 rounded-lg text-sm text-white"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id} className="bg-slate-700 text-white">
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto p-4">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedItems.has(item.path);
              const visibleChildren = hasChildren
                ? item.children!.filter((child) => child.roles.includes(userRole))
                : [];

              if (hasChildren && visibleChildren.length > 0) {
                return (
                  <div key={item.path} className="mb-1">
                    <button
                      onClick={() => toggleExpanded(item.path)}
                      className={`
                        w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                        ${active
                          ? 'bg-white/20 text-white'
                          : 'text-slate-300 hover:bg-white/10 hover:text-white'
                        }
                      `}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-500 pl-2">
                        {visibleChildren.map((child) => {
                          const ChildIcon = child.icon;
                          const childActive = isActive(child.path);
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`
                                flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                                ${childActive
                                  ? 'bg-white/20 text-white'
                                  : 'text-slate-400 hover:bg-white/10 hover:text-white'
                                }
                              `}
                            >
                              <ChildIcon className="mr-3 h-4 w-4" />
                              {child.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    flex items-center px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all duration-200
                    ${active
                      ? 'bg-white/20 text-white'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.label}
                  {item.path === '/chat' && hasChatUnread && (
                    <span className="ml-auto h-2 w-2 bg-red-500 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-slate-600 p-4">
            <button
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="flex w-full items-center px-3 py-2 text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white rounded-lg transition-all duration-200"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-1 flex-col min-w-0 overflow-x-hidden transition-all duration-300 ${isCollapsed ? 'md:pl-16' : 'md:pl-64'}`}>
        <main className="flex-1 min-w-0 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Navigation;